import Button from './Button';

/**
 * Pagination component converted from Django template logic.
 *
 * @param {object} props
 * @param {number} props.currentPage - The current page number.
 * @param {number} props.totalPages - The total number of pages.
 *  * @param {number} props.maxVisible - The max number of visible pages.
 * @param {(page: number) => void} props.onPageChange - Function to call on page click.
 */
export default function Pagination({
    currentPage,
    totalPages,
    maxVisible = 5,
    onPageChange,
}) {
    if (totalPages === 1) {
        return null;
    }

    const pageRange = Array.from({ length: totalPages }, (_, i) => i + 1);

    const hasPrevious = currentPage > 1;
    const hasNext = totalPages ? currentPage < totalPages : true;

    // --- Logic for rendering the page number buttons (center section) ---

    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = startPage + maxVisible - 1;
    if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxVisible + 1);
    }

    const pageNumbersToRender = Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
    );

    const safeOnPageChange = typeof onPageChange === 'function' ? onPageChange : () => { };


    const handleClick = (pageNumber) => {
        const isCurrent = pageNumber === currentPage;
        // Only run if the button is NOT the current page
        if (!isCurrent) {
            safeOnPageChange(pageNumber);
        }
    };


    const PageButton = ({ pageNumber }) => {
        const isCurrent = pageNumber === currentPage;
        return (
            <Button
                hierarchy={isCurrent ? "primary" : "secondary"}
                size="md"
                text={String(pageNumber)}
                className="w-[40px]"
                onClick={() => handleClick(pageNumber)}
            />
        );
    };

    const NavButton = ({ pageNumber, displayText, shouldEnable = false }) => {
        return (<Button
            hierarchy="secondary"
            size="md"
            text={displayText}
            className="w-[88px]"
            disabled={!shouldEnable}
            onClick={() => handleClick(pageNumber)}
        />)
    };

    const EllipsisButton = () => {
        return (<Button
            hierarchy="secondary"
            size="md"
            text="..."
            className="w-[40px]"
            disabled={true}
        />)
    };

    return (
        <div className="pagination">
            {/* Previous Button */}
            <div className="flex gap-3">
                <NavButton pageNumber={currentPage - 1} displayText="ก่อนหน้า" shouldEnable={hasPrevious} />
            </div>

            {/* Page Numbers and Ellipses */}
            <div className="flex gap-3">
                {/* Leading Ellipsis and First Page Button */}
                {startPage > 1 && (
                    <div className="flex gap-3">
                        <PageButton key={1} pageNumber={1} />
                        {startPage > 2 && (
                            <EllipsisButton />
                        )}
                    </div>

                )}

                {/* Page Number Buttons */}
                {pageNumbersToRender.map(pageNumber => (<PageButton key={pageNumber} pageNumber={pageNumber} />))}

                {/* Trailing Ellipsis and Last Page Button */}
                {endPage < totalPages && (
                    <div className="flex gap-3">
                        {endPage < totalPages - 1 && (
                            <EllipsisButton />
                        )}
                        <PageButton key={totalPages} pageNumber={totalPages} />
                    </div>
                )}
            </div>

            {/* Next Button */}
            <div className="flex gap-3">
                <NavButton pageNumber={currentPage + 1} displayText="ถัดไป" shouldEnable={hasNext} />
            </div>
        </div>
    );
}